import type { Menu, Recipe, RecipeUUID, RecipeCoreId } from '@library/menu-service'
import { fromAPIRecipe } from '@library/menu-service'

import { actionTypes } from 'actions/actionTypes'

// Re-export types from menu-service for backwards compatibility
export type { Recipe, RecipeUUID, RecipeCoreId }

export type MenuRecipesState = {
  recipesV3: {
    recipes: Record<RecipeUUID, Recipe>
    coreIdToUUID: Record<RecipeCoreId, RecipeUUID>
  }
}

const recipesInitialState: MenuRecipesState['recipesV3'] = {
  recipes: {},
  coreIdToUUID: {},
}

type Action =
  | {
      type: typeof actionTypes.MENU_SERVICE_V3_DATA_RECEIVED
      response: Menu
    }
  | {
      type: typeof actionTypes.MENU_RECIPE_TILE_RECEIVED
      recipe: Recipe
    }

export const menuRecipesReducers = {
  recipesV3: (state = recipesInitialState, action: Action): MenuRecipesState['recipesV3'] => {
    switch (action.type) {
      case actionTypes.MENU_SERVICE_V3_DATA_RECEIVED: {
        const domainRecipes = Object.values(action.response.recipes).map(fromAPIRecipe)

        const coreIdToUUID: Record<RecipeCoreId, RecipeUUID> = { ...state.coreIdToUUID }
        domainRecipes.forEach((recipe) => {
          coreIdToUUID[recipe.coreId] = recipe.id
        })

        const recipes = Object.fromEntries(
          Object.entries(state.recipes)
            // take existing recipes, mark them as unavailable
            .map(([id, recipe]) => [id, { ...recipe, isAvailable: false, isOffered: false }])
            // add newly loaded recipes (add them at the end to overwrite existing recipes with Object.fromEntries)
            .concat(domainRecipes.map((recipe) => [recipe.id, recipe])),
        )

        return {
          coreIdToUUID,
          recipes,
        }
      }
      case actionTypes.MENU_RECIPE_TILE_RECEIVED: {
        const currentRecipe = state.recipes[action.recipe.id]
        const refreshedRecipe = currentRecipe
          ? {
              ...currentRecipe,
              ...action.recipe,
              isAvailable: currentRecipe.isAvailable,
              isOffered: currentRecipe.isOffered,
            }
          : action.recipe

        return {
          coreIdToUUID: {
            ...state.coreIdToUUID,
            [refreshedRecipe.coreId]: refreshedRecipe.id,
          },
          recipes: {
            ...state.recipes,
            [refreshedRecipe.id]: refreshedRecipe,
          },
        }
      }
      default:
        return state
    }
  },
}
